// Language-aware form controls for authoring template content.
//
// Two presentations, because the two surfaces have very different shapes:
//
//   LanguageTabs   — the EDITOR. One set of fields, a control at the top that
//                    swaps which language they hold. A published rubric runs to
//                    ~35 required strings across its standards and questions;
//                    stacking three inputs per field would turn one long page
//                    into three, and a translation pass is done one language at
//                    a time, top to bottom, anyway.
//   LocalizedStack — the MODALS. Two fields, so all three languages fit at once
//                    and nothing is hidden behind a control the author has to
//                    discover.
//
// The tabs are deliberately NOT wired to i18n.changeLanguage: App.js keys the
// routed tree on the active UI language, so switching it remounts the editor
// and throws away the unsaved draft.
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "../../lang/languages";

// The one field skin these authoring screens share — imported rather than
// re-typed, so the focus ring can't drift between the editor and the modals.
export const inputBase =
  "bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition";

/**
 * Authoring-language switcher with per-language progress.
 *
 * The count is the whole point: on a 131-question rubric "which language still
 * needs work, and how much" is the question the author asks constantly, and it
 * cannot be answered by looking at the fields currently on screen.
 */
export function LanguageTabs({ value, onChange, coverage, disabled = false }) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t("templates.authoringLanguage")}
      className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg"
    >
      {LANGUAGES.map((lang) => {
        const active = lang.id === value;
        const stats = coverage?.[lang.id];
        const showCount = stats && stats.total > 0;
        const complete = showCount && stats.done === stats.total;
        const empty = showCount && stats.done === 0;
        return (
          <button
            key={lang.id}
            type="button"
            onClick={() => onChange(lang.id)}
            disabled={disabled}
            aria-pressed={active}
            title={lang.name}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition disabled:opacity-50 ${
              active
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {lang.code}
            {showCount && (
              <span
                className={`px-1 rounded text-[9px] font-black tabular-nums ${
                  complete
                    ? "bg-emerald-100 text-emerald-700"
                    : empty
                      ? "bg-slate-200 text-slate-500"
                      : "bg-amber-100 text-amber-700"
                }`}
              >
                {stats.done}/{stats.total}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The same field in the other languages, muted, under the input being edited.
 *
 * Without it the editor can only be used to translate from memory: you pick
 * Kazakh and the Russian you are translating FROM disappears from the screen.
 * Each line is a button — clicking copies that text into the field, which for
 * numbers, names and near-identical strings beats retyping.
 */
export function TranslationReference({ value, lang, onCopy }) {
  const { t } = useTranslation();
  const others = LANGUAGES.filter(
    (l) => l.id !== lang && (value?.[l.id] || "").trim(),
  );
  if (!others.length) return null;
  return (
    <div className="mt-1.5 space-y-1">
      {others.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => onCopy(l.id)}
          title={t("templates.copyFromLanguage", { language: l.name })}
          className="group flex w-full items-start gap-2 text-left rounded-md px-1 py-0.5 hover:bg-slate-50 transition"
        >
          <span className="mt-px text-[9px] font-black uppercase tracking-wider text-slate-300 group-hover:text-indigo-400 shrink-0 w-7">
            {l.code}
          </span>
          <span className="text-[11px] leading-snug text-slate-500 group-hover:text-slate-600">
            {value[l.id]}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * One translated field as three labelled rows — for short forms where hiding
 * two thirds of the content behind a tab would cost more than it saves.
 */
export function LocalizedStack({
  label,
  value,
  onChange,
  placeholder,
  error = false,
  autoFocus = false,
  multiline = false,
}) {
  const Control = multiline ? "textarea" : "input";
  return (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
        {label}
      </label>
      <div className="space-y-1.5">
        {LANGUAGES.map((lang, index) => (
          <div key={lang.id} className="flex items-start gap-2">
            <span
              className="mt-2 w-8 shrink-0 text-[10px] font-black uppercase tracking-wider text-slate-500"
              aria-hidden="true"
            >
              {lang.code}
            </span>
            <Control
              {...(multiline ? { rows: 2 } : { type: "text" })}
              value={value?.[lang.id] || ""}
              autoFocus={autoFocus && index === 0}
              onChange={(e) => onChange({ ...value, [lang.id]: e.target.value })}
              placeholder={index === 0 ? placeholder : undefined}
              aria-label={`${label} — ${lang.name}`}
              className={`flex-1 min-w-0 px-3 py-2 text-sm ${inputBase} ${
                error
                  ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/20"
                  : ""
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
